import { normalizeImageFile } from './heic';
// Host media limits + client-side helpers. Kept in one place so the UI copy,
// the compression behavior, and the video pre-checks can never drift apart.
//
// Photos: compressed client-side (canvas resize + re-encode) before upload —
// this makes the "limit" mostly invisible to hosts; the server-side bucket
// limit (see the host_media_limits migration) is a backstop, not the primary
// control.
//
// Video: NOT re-encoded client-side (real transcoding needs a server job or a
// heavy WASM library — disproportionate here). Instead we validate size and
// duration up front and surface a specific, actionable error before even
// attempting the upload.

// Every host photo (avatar, moodboard) only ever renders inside a grid tile
// or a small circular avatar — nothing in the app opens a photo full-screen —
// so the old 1920px/0.82 master was several times larger than anything on
// screen ever uses. 960px comfortably covers even a large tile at 2x retina
// with real headroom; the JPEG quality drop is invisible at that size but
// meaningfully smaller on the wire, which is what was making moodboard grids
// slow to load, especially on mobile.
export const PHOTO_MAX_DIMENSION_PX = 960;
export const PHOTO_JPEG_QUALITY = 0.75;
/** Server-side backstop (see migration) — client compression should stay well under this. */
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

export const VIDEO_MAX_BYTES = 150 * 1024 * 1024;
export const VIDEO_MAX_DURATION_SECONDS = 90;

export const PHOTO_LIMIT_LABEL = `Photos are optimized automatically, up to ${PHOTO_MAX_DIMENSION_PX}px.`;
export const VIDEO_LIMIT_LABEL = `Up to ${VIDEO_MAX_BYTES / (1024 * 1024)}MB and ${VIDEO_MAX_DURATION_SECONDS} seconds.`;

/**
 * Resize + re-encode an image client-side so it never needs the server-side
 * limit to reject it. Downscales to at most PHOTO_MAX_DIMENSION_PX on the
 * longest edge (this app never displays photos larger than that) and
 * re-encodes as JPEG. Non-image files pass through unchanged.
 */
export async function compressImage(original: File): Promise<File> {
  // A HEIC (iPhone) photo becomes a JPEG first; the browser can't read it otherwise.
  const file = await normalizeImageFile(original);
  if (!file.type.startsWith('image/')) return file;

  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file; // Unsupported format (e.g. HEIC in some browsers) — let the upload attempt as-is.

  const scale = Math.min(1, PHOTO_MAX_DIMENSION_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', PHOTO_JPEG_QUALITY)
  );
  if (!blob) return file;

  // Only use the compressed version if it's actually smaller — a tiny source
  // image re-encoded at quality 0.82 can occasionally grow.
  if (blob.size >= file.size) return file;

  const newName = file.name.replace(/\.\w+$/, '') + '.jpg';
  return new File([blob], newName, { type: 'image/jpeg' });
}

/** Reads a video file's duration without uploading it, for pre-upload validation. */
function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read video metadata'));
    };
    video.src = url;
  });
}

export interface VideoValidation {
  ok: boolean;
  error?: string;
}

/** Checks a video against the size + duration limits, with a specific reason on failure. */
export async function validateVideo(file: File): Promise<VideoValidation> {
  if (file.size > VIDEO_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(0);
    return { ok: false, error: `This video is ${mb}MB — please trim or compress it to under ${VIDEO_MAX_BYTES / (1024 * 1024)}MB.` };
  }
  try {
    const duration = await readVideoDuration(file);
    if (Number.isFinite(duration) && duration > VIDEO_MAX_DURATION_SECONDS) {
      return { ok: false, error: `This video is ${Math.round(duration)}s long — please trim it to under ${VIDEO_MAX_DURATION_SECONDS} seconds.` };
    }
  } catch {
    // Couldn't read metadata (unsupported format/codec) — let Storage's own
    // limit be the backstop rather than blocking a possibly-valid upload.
  }
  return { ok: true };
}
